import { st, classes, cssStates } from './BaseCalendar.st.css';
import React from 'react';
import { DayPicker, DayFlag, UI, SelectionState } from 'react-day-picker';
import * as locales from 'date-fns/locale';
import { endOfMonth, isAfter, isBefore, startOfMonth } from 'date-fns';
import { localeUtilsFactory } from 'wix-style-react-tpa';
import { shellClasses } from '../../../../common/shellClasses';
class BaseCalendar extends React.PureComponent {
    constructor() {
        super(...arguments);
        this.extractFullDate = (date) => {
            return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
        };
        this._handleDayClick = (value, modifiers, event = null) => {
            this._preventActionEventDefault(event);
            const { onChange, shouldCloseOnSelect } = this.props;
            onChange(value || this.props.value, modifiers, event);
            shouldCloseOnSelect && this.props.onClose(event);
        };
        this._preventActionEventDefault = (event = null) => {
            if (event &&
                (!event.key || (event.key !== 'Escape' && event.key !== 'Tab'))) {
                event.preventDefault();
            }
        };
        this._createDayPickerProps = () => {
            const { filterDate, excludePastDates, numOfMonths, firstDayOfWeek, rtl, today, onDisplayedViewChange, displayedMonth, captionElement, allowSelectingOutsideDays, size, fixedWeeks, showOutsideDays, } = this.props;
            const locale = locales[this._getLocale().replace('-', '')] || locales.enUS;
            const value = BaseCalendar.parseValue(this.props.value);
            const todayDate = today || new Date();
            const localeUtils = localeUtilsFactory(this._getLocale(), firstDayOfWeek);
            const firstOfMonth = [
                new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1),
                new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 1),
            ];
            const lastOfMonth = [
                new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 0),
                new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 2, 0),
            ];
            const firstOfMonthClass = st(classes.day, { firstOfMonth: true });
            const lastOfMonthClass = st(classes.day, { lastOfMonth: true });
            const modifiers = {
                firstOfMonth,
                lastOfMonth,
                [firstOfMonthClass]: firstOfMonth,
                [lastOfMonthClass]: lastOfMonth,
                [classes.past]: { before: BaseCalendar.parseValue(today) },
                ...this.props.modifiers,
            };
            // We must add the dummy state since ReactDayPicker use it as a selector in their code
            const outsideCssState = allowSelectingOutsideDays
                ? cssStates({ dummyOutside: true })
                : cssStates({ outside: true });
            return {
                autoFocus: this.props.autoFocus,
                showOutsideDays,
                disabled: new Array((date) => !filterDate(date), excludePastDates ? { before: todayDate } : undefined).filter(Boolean),
                defaultMonth: displayedMonth,
                selected: value,
                month: displayedMonth,
                locale,
                fixedWeeks,
                today: todayDate,
                mode: 'single',
                onSelect: (selected, _, _modifiers, event) => this._handleDayClick(selected, _modifiers, event),
                components: {
                    MonthCaption: () => null,
                    Nav: () => captionElement,
                },
                formatters: {
                    formatWeekdayName: (date) => localeUtils.formatWeekdayShort(date.getDay(), this._getLocale()),
                    // @ts-expect-error - https://github.com/wix-private/wix-ui-tpa/pull/628#discussion_r1986316238
                    formatDay: (date, options, dateLib) => {
                        const { dateIndication } = this.props;
                        const isOutsideDay = this.isDateOutsideOfMonth(date);
                        const isSelectedDay = this.extractFullDate(value) === this.extractFullDate(date);
                        const dateIndicationNode = dateIndication?.({
                            date,
                            isSelected: isSelectedDay,
                        });
                        const hasIndication = dateIndicationNode && !isOutsideDay;
                        return (React.createElement(React.Fragment, null,
                            React.createElement("span", { className: st(classes.dayText, { hasIndication }) }, date.getDate()),
                            hasIndication ? (React.createElement("div", { className: classes.dayIndicationContainer }, dateIndicationNode)) : null));
                    },
                },
                weekStartsOn: localeUtils.getFirstDayOfWeek(),
                numberOfMonths: numOfMonths,
                modifiers,
                modifiersClassNames: {
                    firstOfMonth: 'firstOfMonth',
                    lastOfMonth: 'lastOfMonth',
                    [firstOfMonthClass]: firstOfMonthClass,
                    [lastOfMonthClass]: lastOfMonthClass,
                },
                dir: rtl ? 'rtl' : 'ltr',
                classNames: {
                    /* The classes: 'DayPicker', 'DayPicker-wrapper', 'DayPicker-Month', 'DayPicker-Day', 'disabled'
                      are used as selectors for the elements at the drivers and at the e2e tests */
                    [UI.Root]: st(classes.container, { size }, 'DayPicker'),
                    [UI.Months]: st(classes.months, { twoMonths: numOfMonths > 1 }, 'DayPicker-wrapper'),
                    [UI.Month]: st(classes.month, { size }),
                    [UI.DayButton]: st(classes.dayWrapper, shellClasses.hasCustomFocus),
                    [UI.Weekdays]: st(classes.weekdays),
                    [UI.Weekday]: st(classes.weekday, { size }),
                    [UI.MonthGrid]: st(classes.body, { size }, classes.monthsGrid),
                    [UI.Week]: st(classes.week),
                    [UI.Day]: st(classes.day, { size }, 'DayPicker-Day'),
                    [DayFlag.focused]: st(classes.focused),
                    [DayFlag.today]: st(classes.today),
                    [SelectionState.selected]: st(classes.day, { selected: true }, 
                    // TODO: JB: is redundant (?)
                    cssStates({ selected: true })),
                    [DayFlag.disabled]: st('disabled', cssStates({ disabled: true })),
                    [DayFlag.outside]: outsideCssState,
                },
                onMonthChange: onDisplayedViewChange,
            };
        };
    }
    static parseValue(value) {
        if (value && value instanceof Date) {
            return new Date(value);
        }
        return new Date();
    }
    isDateOutsideOfMonth(date) {
        const start = startOfMonth(this.props.displayedMonth);
        const end = endOfMonth(this.props.displayedMonth);
        return isBefore(date, start) || isAfter(date, end);
    }
    _getLocale() {
        return this.props.locale || this.context.locale || 'en-US';
    }
    render() {
        const { dataHook, className, size } = this.props;
        return (React.createElement("div", { "data-hook": dataHook, "data-size": size, className: st(classes.root, className), onClick: this._preventActionEventDefault, tabIndex: -1 },
            React.createElement(DayPicker, { ...this._createDayPickerProps() })));
    }
}
BaseCalendar.displayName = 'BaseCalendar';
BaseCalendar.defaultProps = {
    className: '',
    filterDate: () => true,
    dateIndication: () => null,
    shouldCloseOnSelect: true,
    onClose: () => { },
    autoFocus: true,
    excludePastDates: false,
    // @ts-expect-error
    showMonthDropdown: false,
    showYearDropdown: false,
    numOfMonths: 1,
    size: 'medium',
    allowSelectingOutsideDays: false,
    fixedWeeks: true,
};
BaseCalendar.nextDay = (date) => {
    const day = new Date(date);
    day.setDate(day.getDate() + 1);
    return day;
};
BaseCalendar.prevDay = (date) => {
    const day = new Date(date);
    day.setDate(day.getDate() - 1);
    return day;
};
export default BaseCalendar;
//# sourceMappingURL=BaseCalendar.js.map